/**
 * Render de una entrada del timeline. Soporta:
 *  - Acciones automáticas (cambio de estado, técnico asignado, etc.) con
 *    old_value → new_value.
 *  - Comentarios técnicos manuales (texto libre).
 *  - "Mensaje al cliente enviado" con adjuntos embebidos en base64.
 *  - "Aviso al cliente …" (iter 68): muestra destinatario + estado, NUNCA HTML.
 *
 * Sanitización defensiva (iter 68): si `entry.note` parece HTML
 * (contiene `<` y `>`), NO se renderiza; en su lugar, mostramos
 * "[contenido HTML oculto — editar plantilla en Configuración > Diseño]".
 * Esto cubre entradas antiguas que persistieron el HTML completo del email.
 *
 * No depende de hooks ni de la API: es presentacional puro.
 */
function _looksLikeHtml(s) {
  if (!s || typeof s !== "string") return false;
  // heurística simple: contiene una etiqueta cerrada o un doctype
  return /<\/?[a-z][\s\S]*?>/i.test(s) || /<!doctype/i.test(s);
}

export default function TimelineEntryItem({ entry }) {
  const at = entry.at ? new Date(entry.at).toLocaleString("es") : "";
  const isComment = entry.action === "Comentario técnico";
  const isMessage = entry.action === "Mensaje al cliente enviado";
  const isNotify = typeof entry.action === "string" && entry.action.startsWith("Aviso al cliente");
  const hasDiff =
    (entry.old_value !== undefined && entry.old_value !== null && entry.old_value !== "") ||
    (entry.new_value !== undefined && entry.new_value !== null && entry.new_value !== "");

  // Iter 170 — clasificación de comentarios manuales (Facturación / Laboratorio / ambos)
  const billing = isComment && !!entry.is_billing;
  const lab = isComment && !!entry.is_laboratory;
  const tagClass = billing && lab ? "tl-entry-both" : billing ? "tl-entry-billing" : lab ? "tl-entry-lab" : "";
  const accent = tagClass
    ? tagClass
    : isComment
    ? "border-l-4 border-[#6E8B3D] bg-[#F0F4E8]"
    : isMessage || isNotify
    ? "border-l-4 border-blue-500 bg-blue-50"
    : "border-l-4 border-[#E5E5E0] bg-white";

  // Decidimos si renderizamos la nota:
  //  - Para comentarios técnicos manuales y mensajes libres: SÍ (texto plano).
  //  - Para "Aviso al cliente …" mostramos un resumen (subject) que el backend
  //    ya pone como note plano ('Email enviado: "..."').
  //  - Si la nota parece HTML (entradas legacy), la ocultamos.
  const noteLooksLikeHtml = _looksLikeHtml(entry.note);
  const shouldShowNote = entry.note && !noteLooksLikeHtml;

  return (
    <div className={`${accent} px-3 py-2`} data-testid="timeline-entry" data-billing={billing || undefined} data-laboratory={lab || undefined}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          {at}
          {entry.by ? ` · ${entry.by}` : ""}
        </div>
        <div className="flex items-center gap-2">
          {tagClass && (
            <span className={`tl-tag ${billing && lab ? "tl-tag-both" : billing ? "tl-tag-billing" : "tl-tag-lab"}`} data-testid="timeline-entry-tag">
              {billing && lab && <span className="half" />}
              {billing && lab ? "Facturación · Laboratorio" : billing ? "Facturación" : "Laboratorio"}
            </span>
          )}
          <div className="font-display text-xs font-bold">{entry.action}</div>
        </div>
      </div>
      {hasDiff && entry.action !== "Comentario técnico" && (
        <div className="mt-1 font-mono text-[11px] flex flex-wrap items-center gap-2">
          {entry.old_value ? (
            <span className="line-through text-[#A8A8A0]">{entry.old_value}</span>
          ) : null}
          {entry.old_value && entry.new_value ? <span className="text-[#525252]">→</span> : null}
          {entry.new_value ? (
            <span className="font-bold text-[#111110]" data-testid="timeline-entry-target">
              {entry.new_value}
            </span>
          ) : null}
        </div>
      )}
      {isNotify && entry.status && (
        <div className="mt-1 text-[11px] font-mono text-[#525252]">
          Estado: <span className="font-bold text-[#111110]">{entry.status}</span>
        </div>
      )}
      {shouldShowNote && (
        <div
          className="mt-1 text-sm whitespace-pre-wrap text-[#111110]"
          data-testid="timeline-entry-note"
        >
          {entry.note}
        </div>
      )}
      {noteLooksLikeHtml && (
        <div
          className="mt-1 text-[11px] italic text-[#525252]"
          data-testid="timeline-entry-note-hidden"
        >
          [contenido HTML oculto — la plantilla se gestiona en Configuración &gt; Diseño &gt; Emails]
        </div>
      )}
      {Array.isArray(entry.attachments) && entry.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {entry.attachments.map((a, idx) => {
            const stableKey = `${a.name || "att"}-${idx}-${(a.data_b64 || "").slice(0, 16)}`;
            return (
              <a
                key={stableKey}
                href={`data:${a.content_type || "image/jpeg"};base64,${a.data_b64}`}
                target="_blank"
                rel="noreferrer"
                className="block border border-[#E5E5E0] hover:border-[#111110]"
                data-testid="timeline-entry-attachment"
              >
                <img
                  src={`data:${a.content_type || "image/jpeg"};base64,${a.data_b64}`}
                  alt={a.name || `Adjunto ${idx + 1}`}
                  className="h-16 w-16 object-cover"
                />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
